import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { IAttachment, IPresignedUrlResponse } from '../models/attachment.model';
import { GlobalConfigService } from './global-config.service';

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {
  private get baseUrl(): string {
    return this.globalConfig.getApiUrl();
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService
  ) {}

  /**
   * Solicita presigned URL para upload direto no S3.
   * POST /incidents/attachments/generate-upload-url
   * Envia um único arquivo por vez (file_name, content_type).
   */
  generateUploadUrl(
    file: { file_name: string; content_type: string; treatment_plan_id?: string }
  ): Promise<IPresignedUrlResponse> {
    return lastValueFrom(
      this.http.post<IPresignedUrlResponse>(
        `${this.baseUrl}/incidents/attachments/generate-upload-url`,
        file
      )
    );
  }

  /**
   * Faz upload direto para o S3 usando a presigned URL.
   * Usa fetch nativo para evitar interferência do interceptor Angular
   * e garantir que apenas o Content-Type correto seja enviado.
   */
  async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    if (!response.ok) {
      throw new Error(`Erro ao fazer upload para o S3: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Registra metadados do anexo no backend após upload concluído no S3.
   * POST /treatment-plans/{treatmentPlanId}/incident-report/attachments
   * O backend espera uma lista de anexos (List<IncidentAttachmentSaveRequest>).
   */
  registerAttachment(
    treatmentPlanId: string,
    data: {
      file_name: string;
      file_type: string;
      file_size: number;
      storage_key: string;
      attachment_order: number;
    }
  ): Promise<IAttachment> {
    return lastValueFrom(
      this.http.post<IAttachment[]>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report/attachments`,
        [data]
      )
    ).then(list => {
      if (!Array.isArray(list) || list.length === 0) {
        throw new Error('Resposta inválida ao registrar anexo: nenhum anexo retornado pelo backend.');
      }

      return list[0];
    });
  }

  /**
   * Lista os anexos de intercorrência de um plano de tratamento.
   * GET /treatment-plans/{treatmentPlanId}/incident-report/attachments
   */
  getAttachments(treatmentPlanId: string): Promise<IAttachment[]> {
    return lastValueFrom(
      this.http.get<IAttachment[]>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report/attachments`
      )
    );
  }

  /**
   * Remove um anexo do S3 e do banco.
   * DELETE /treatment-plans/{treatmentPlanId}/incident-report/attachments/{attachId}
   */
  deleteAttachment(treatmentPlanId: string, attachmentId: string): Promise<void> {
    return lastValueFrom(
      this.http.delete<void>(
        `${this.baseUrl}/treatment-plans/${treatmentPlanId}/incident-report/attachments/${attachmentId}`
      )
    );
  }
}
